<template>

  <section id="jobs-done-container" :class="{ active: gameStore.showJobs }">
    <button id="show-jobs" @click="gameStore.toggleShowJobs" :disabled="!gameStore.gameStarted && gameStore.jobsDone.length === 0"> <span v-if="gameStore.showJobs">
    Hide Jobs<i class="fa-solid fa-circle-chevron-down"></i>
  </span>
  <span v-else>
    Show Jobs <i class="fa-solid fa-circle-chevron-up"></i>
  </span>
</button>
    <div id="jobs-done-top">
      <h2 class="completed-cards">Completed Jobs: {{ filledCount }}</h2>
    </div>
    <div id="scroller">
      <div
        id="jobs-done-wrapper"
        @dragover.prevent="handleDragOver"
        @drop="handleDrop($event, hoveredSlotIndex || null)"
      >
        <span class="priority-label high">
          <i class="fa-solid fa-arrow-left"></i>Higher Priority
        </span>

        <div
          class="slot"
          v-for="(slot, index) in slots"
          :key="slot.id"
          :class="{ placeholder: !slot.card, 'over-slot': hoveredSlotIndex === index }"
          @dragenter="handleDragEnter(index)"
          @dragleave="handleDragLeave(index)"
          @dragover.prevent="handleDragOver"
          @drop="handleDrop($event, index)"
        >
          <div v-if="!slot.card" class="placeholder-card">
            <div class="placeholder-content"></div>
          </div>

          <GameCard
            v-if="slot.card"
            :key="slot.card.id"
            :title="slot.card.title"
            :content="slot.card.content"
            :ranking="slot.card.ranking"
            :explanation="slot.card.explanation"
            :frontImage="slot.card.frontImage"
            
            :primaryColor="slot.card.primaryColor"
            :secondaryColor="slot.card.secondaryColor"
            :isFlipped="true"
            :isInJobsDone="true"
            draggable="true"
            @dragstart="handleInternalDragStart($event, index)"
            @show-explanation="forwardShowExplanation"
          />
        </div>

        <span class="priority-label low">
          Lower Priority<i class="fa-solid fa-arrow-right"></i>
        </span>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import { useGameStore } from '@/stores/gameStore';
import GameCard from './GameCard.vue';

const gameStore = useGameStore();

const slots = computed(() => {
  return gameStore.jobsDone.map((card, index) => ({
    id: `slot-${index + 1}`,
    card: card || null,
  }));
});

// FLIP animation for smooth reordering in jobs done
const animateJobsReorder = async (fromIndex, toIndex) => {
  const jobSlots = document.querySelectorAll('#jobs-done-wrapper .slot');
  const fromSlot = jobSlots[fromIndex];
  const toSlot = jobSlots[toIndex];
  
  if (!fromSlot || !toSlot) return;
  
  // Get the cards in both slots BEFORE any changes
  const fromCard = fromSlot.querySelector('.card');
  const toCard = toSlot.querySelector('.card');
  
  if (!fromCard) return;
  
  // Store initial positions (First)
  const fromRect = fromCard.getBoundingClientRect();
  const toRect = toCard ? toCard.getBoundingClientRect() : toSlot.getBoundingClientRect();
  
  // Immediately perform the swap in the DOM (Last)
  let updatedJobsDone = [...gameStore.jobsDone];
  const draggedCard = updatedJobsDone[fromIndex];
  const targetCard = updatedJobsDone[toIndex];
  updatedJobsDone[fromIndex] = targetCard;
  updatedJobsDone[toIndex] = draggedCard;
  gameStore.updateJobsDone([...updatedJobsDone]);
  
  // Wait for DOM update
  await nextTick();
  
  // Get new positions after swap (Invert)
  const newFromCard = fromSlot.querySelector('.card');
  const newToCard = toSlot.querySelector('.card');
  
  if (newFromCard) {
    const newFromRect = newFromCard.getBoundingClientRect();
    const deltaX = fromRect.left - newFromRect.left;
    const deltaY = fromRect.top - newFromRect.top;
    
    // Apply inverse transform to make it appear in old position
    newFromCard.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    newFromCard.style.transition = 'none';
  }
  
  if (newToCard) {
    const newToRect = newToCard.getBoundingClientRect();
    const deltaX = toRect.left - newToRect.left;
    const deltaY = toRect.top - newToRect.top;
    
    // Apply inverse transform to make it appear in old position
    newToCard.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    newToCard.style.transition = 'none';
  }
  
  // Force reflow
  if (newFromCard) newFromCard.offsetHeight;
  if (newToCard) newToCard.offsetHeight;
  
  // Play the animation (Play)
  return new Promise(resolve => {
    if (newFromCard) {
      newFromCard.style.transition = 'transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)';
      newFromCard.style.transform = 'translate(0, 0)';
    }
    
    if (newToCard) {
      newToCard.style.transition = 'transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)';
      newToCard.style.transform = 'translate(0, 0)';
    }
    
    setTimeout(() => {
      // Clean up
      if (newFromCard) {
        newFromCard.style.transform = '';
        newFromCard.style.transition = '';
      }
      if (newToCard) {
        newToCard.style.transform = '';
        newToCard.style.transition = '';
      }
      resolve();
    }, 400);
  });
};

const hoveredSlotIndex = ref(null);
const emit = defineEmits(['show-explanation']);


const forwardShowExplanation = (explanationData) => {
  emit('show-explanation', explanationData); // Forward event to HomeView
};
const updateSlots = () => {
  for (let i = 0; i < slots.value.length; i++) {
    slots.value[i].card = gameStore.jobsDone[i] || null;
  }
};
watch(
  () => gameStore.jobsDone,
  () => updateSlots(),
  { immediate: true, deep: true }
);

const filledCount = computed(() =>
  slots.value.reduce((count, slot) => (slot.card ? count + 1 : count), 0)
);

// ---- Internal Dragstart for JobsDone ----
const handleInternalDragStart = (event, index) => {
  console.log("JobsDone: Internal drag start triggered for slot index:", index);
  if (!slots.value[index].card) {
    console.warn("JobsDone: No card in slot", index);
    return;
  }
  event.dataTransfer.effectAllowed = "move";
  const dragData = {
    source: "internal",
    index: index,
    card: slots.value[index].card,
  };
  event.dataTransfer.setData("text/plain", JSON.stringify(dragData));
  console.log("JobsDone: Drag data set:", dragData);
};

// ---- Drag enter/leave handlers ----
const handleDragEnter = (index) => {
  hoveredSlotIndex.value = index;
  console.log("JobsDone: Drag entered slot:", index);
};

const handleDragLeave = (index) => {
  if (hoveredSlotIndex.value === index) {
    hoveredSlotIndex.value = null;
    console.log("JobsDone: Drag left slot:", index);
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
};

// ---- Drop Handler for Internal Reordering ----

const handleDrop = async (event, targetIndex) => {
  event.preventDefault();
  // If targetIndex is null, use the hoveredSlotIndex value as fallback
  if (targetIndex == null) {
    targetIndex = hoveredSlotIndex.value;
  }
  if (targetIndex == null) {
    console.warn("No valid drop target index; ignoring drop.");
    return;
  }
  hoveredSlotIndex.value = null;
  
  console.log("JobsDone: Drop triggered at target index:", targetIndex);

  // Retrieve drag data from "text/plain"
  const dataStr = event.dataTransfer.getData("text/plain");
  if (!dataStr) {
    console.error("JobsDone: No drag data found");
    return;
  }
  let dragData;
  try {
    dragData = JSON.parse(dataStr);
  } catch (err) {
    console.error("JobsDone: Error parsing drag data:", err);
    return;
  }
  console.log("JobsDone: Parsed drag data:", dragData);

  // Create a copy of the current jobsDone array and ensure it has a fixed length with null placeholders
  let updatedJobsDone = [...gameStore.jobsDone];
  while (updatedJobsDone.length < gameStore.maxJobsDoneCount) {
    updatedJobsDone.push(null);
  }
  console.log("JobsDone: Array before drop:", updatedJobsDone);

  if (dragData.source === "external") {
    // External drop: card coming from CardContainer
    // If the target slot is occupied, find the first empty slot.
    let dropIndex = targetIndex;
    if (updatedJobsDone[dropIndex] !== null) {
      console.warn("JobsDone: Target slot", dropIndex, "is occupied. Searching for an empty slot.");
      const emptyIndex = updatedJobsDone.findIndex(slot => slot === null);
      if (emptyIndex === -1) {
        console.error("JobsDone: No empty slot available for external drop.");
        return;
      }
      dropIndex = emptyIndex;
    }
    console.log("JobsDone: Inserting external card at slot index:", dropIndex);
    updatedJobsDone[dropIndex] = dragData.card;
    // Remove the card from the cards-container
    gameStore.removeCardFromDeck(dragData.card.id);
  } else if (dragData.source === "internal") {
    // Internal move: reordering within JobsDone
    const fromIndex = Number(dragData.index);
    if (isNaN(fromIndex) || fromIndex === targetIndex) {
      console.warn("JobsDone: Dropped on the same slot or invalid index. No move performed.");
      return;
    }
    console.log("JobsDone: Internal move from index", fromIndex, "to", targetIndex);
    
    // Use animated reordering instead of direct array manipulation
    try {
      await animateJobsReorder(fromIndex, targetIndex);
      console.log("JobsDone: Animation completed successfully");
    } catch (error) {
      console.error("JobsDone: Animation failed:", error);
    }
    return; // Exit early since animation handles the update
  } else {
    console.error("JobsDone: Unknown drag source:", dragData.source);
    return;
  }

  console.log("JobsDone: Updated array after drop:", updatedJobsDone);
  // Update the store with a new array reference so Vue reactivity picks up the change
  gameStore.updateJobsDone([...updatedJobsDone]);
};



</script>

<style src="../styles/_jobsDone.scss" lang="scss"></style>
